import React, {useState, useEffect, useRef} from "react";
import tw from "twin.macro";
import styled, {createGlobalStyle, css} from "styled-components";
import {motion} from "framer-motion";
import {ReactComponent as PlusIconSVG} from "../../images/icon/PlusIcon.svg";
import {useNavigate} from "react-router-dom";
import {getAuth, signOut} from "firebase/auth";
import {useAuth} from "../../context/AuthContext";
import {db} from "../../FireBaseConfig";
import {collection, query, where, onSnapshot} from "firebase/firestore";
import NavToggler from "../features/NavToggler";
import MobileHeader from "./MobileHeader";
import {useTranslation} from "react-i18next";

const GlobalStyle = createGlobalStyle`
    @font-face {
        font-family: 'SFUIText';
        font-weight: 300;
        font-style: normal;
    }

    body, html {
        margin: 0;
        padding: 0;
        font-family: 'SFUIText', sans-serif;
    }
`;

const Header = styled.header`
    ${tw`hidden lg:flex items-center bg-[#F9F9F9] h-[70px] justify-between gap-4 w-full px-4 lg:px-[9rem] border-b border-[#E0E0E0] box-border`}
`;



const NavContainer = styled.div`
    ${tw`flex items-center gap-6 whitespace-nowrap`}
    flex: 1;
    justify-content: space-between;
`;
const NavLinks = styled.div`
    ${tw`flex items-center gap-5 whitespace-nowrap`}
    flex-shrink: 0;
    min-width: 600px;
`;
const NavLink = styled.a`
    ${tw`text-sm tracking-wide transition duration-300`}
    text-decoration: none;
    color: #2D2D2D;
    padding: 6px 10px;
    display: inline-block;
    text-align: center;

    &:hover, &:focus {
        color: #0ABD19;
        text-decoration: underline;
    }
`;
const LastNavLink = styled(NavLink)`${tw`mr-0`}`;
const ProfileDropdownContainer = tw.div`relative inline-block text-left mt-4 lg:mt-0`;
const DropdownMenu = styled(motion.div)`
    ${tw`origin-top-right absolute right-0 mt-2 w-48 lg:w-64 max-w-[90vw] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50`}
`;

const DropdownItem = styled.a`
    ${tw`block px-4 py-3 text-sm text-gray-700 transition-colors duration-150`}
    text-decoration: none;

    &:hover {
        ${tw`bg-green-100 text-green-600`}
    }
`;
const ProfileButton = styled.button`
    ${tw`flex items-center text-sm font-medium text-white rounded-full focus:outline-none ml-2 lg:ml-4`}
    ${tw`w-[120px] lg:w-[180px] bg-green-500 py-1 justify-center transition duration-300 whitespace-nowrap`}
    background-color: #0ABD19;
    border: none;

    &:hover, &:focus {
        transform: scale(1.05);
    }
`;

const InitialsCircle = styled.div`
    ${tw`flex items-center justify-center w-8 h-8 rounded-full bg-white text-green-500 mr-2`}
    font-size: 14px;
    padding: 2px;
    font-weight: normal;
`;
const IconContainer = styled.div`${tw`flex items-center cursor-pointer`}`;
const LanguageSwitcher = styled.div`
    ${tw`flex items-center gap-2 whitespace-nowrap`}
    min-width: 80px;
    margin-left: auto;
`;
const LanguageButton = styled.button`
    ${tw`px-2 py-1 text-sm font-medium border rounded focus:outline-none transition duration-300`}
    ${({active}) => active ? tw`bg-[#0ABD19] text-white border-[#0ABD19]` : tw`bg-white text-[#1d324f] border-gray-300 hover:bg-gray-100`}
    width: 36px;
    min-width: 36px;
`;
const PlusIcon = styled(PlusIconSVG)`
    ${({iconMargin}) => iconMargin && css`margin: ${iconMargin};`}
    ${tw`w-5 h-5 min-w-[1rem]`}
`;

const BalanceText = styled.span`
    ${({textMargin}) => textMargin && css`margin: ${textMargin};`}
    ${tw`text-sm whitespace-nowrap`}
`;

const BalanceAndProfileContainer = styled.div`
    ${tw`flex items-center gap-4`}
    min-width: 260px;
    justify-content: flex-end;
    margin-left: 100px;
`;

const HeaderContainer = () => {
    const {isAuthenticated, user} = useAuth();
    const {t, i18n} = useTranslation();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [userData, setUserData] = useState(() => {
        try {
            const stored = localStorage.getItem("userData");
            return stored && stored !== "undefined" ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });
    const [balance, setBalance] = useState(() => {
        const raw = localStorage.getItem("balance");
        return raw && raw !== "undefined" ? parseFloat(raw) : 0.0;
    });

    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const unsubscribeUserRef = useRef(null);
    const unsubscribeBalanceRef = useRef(null);

    useEffect(() => {
        if (user?.email) {
            const normalizedEmail = user.email.toLowerCase();
            const userQuery = query(collection(db, "users"), where("email", "==", normalizedEmail));

            unsubscribeUserRef.current = onSnapshot(userQuery, (snapshot) => {
                if (!snapshot.empty) {
                    const userDoc = snapshot.docs[0];
                    const data = {...userDoc.data(), id: userDoc.id};
                    setUserData(data);
                    localStorage.setItem("userData", JSON.stringify(data));

                    const balanceQuery = query(collection(db, "balances"), where("userId", "==", userDoc.id));
                    if (unsubscribeBalanceRef.current) unsubscribeBalanceRef.current();
                    unsubscribeBalanceRef.current = onSnapshot(balanceQuery, (s) => {
                        if (!s.empty) {
                            const balanceDoc = s.docs[0];
                            const newBalance = balanceDoc.data().balance || 0.0;
                            setBalance(newBalance);
                            localStorage.setItem("balance", newBalance.toString());
                        } else {
                            setBalance(0.0);
                            localStorage.setItem("balance", "0");
                        }
                    });
                }
            });
        }
        return () => {
            if (unsubscribeUserRef.current) unsubscribeUserRef.current();
            if (unsubscribeBalanceRef.current) unsubscribeBalanceRef.current();
        };
    }, [user]);

    const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsDropdownOpen(false);
    };
    useEffect(() => {
        if (isDropdownOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isDropdownOpen]);

    const handleLogout = () => {
        const auth = getAuth();
        signOut(auth).then(() => navigate("/")).catch(console.error);
    };

    const getInitials = () => {
        if (userData) {
            return `${userData.firstName?.[0]?.toUpperCase() || ""}${userData.lastName?.[0]?.toUpperCase() || ""}`;
        }
        return t("header.initialFallback");
    };

    const handlePlusIconClick = () => {
        // Переходим на страницу Personal Area с параметром для автофокуса на пополнении
        navigate("/PersonalArea", { state: { scrollToTopUp: true } });
    };

    const changeLang = (lng) => {
        i18n.changeLanguage(lng);
        document.documentElement.lang = lng;
    };
    const currentLng =
        i18n.language?.startsWith("kz") ? "kz" :
            i18n.language?.startsWith("ru") ? "ru" :
                i18n.resolvedLanguage || "ru";
    return (
        <>
            <GlobalStyle/>
            {isAuthenticated && (
                <>

                    <div className="hidden lg:block w-full">
                    <Header>
                        <NavContainer>
                            <NavLinks>
                                <NavLink href="/CustomRegulations">{t("header.nav.customs")}</NavLink>
                                <NavLink href="/DeliveryCalculator">{t("header.nav.calc")}</NavLink>
                                <NavLink href="/ProhibitedProductsPage">{t("header.nav.prohibited")}</NavLink>
                                <NavLink href="/Contacts">{t("header.nav.question")}</NavLink>
                                <LastNavLink href="/RedemptionOfGoods">{t("header.nav.buyout")}</LastNavLink>
                            </NavLinks>
                            <LanguageSwitcher>
                                <LanguageButton active={currentLng === "ru"}
                                                onClick={() => changeLang("ru")}>RU</LanguageButton>
                                <LanguageButton active={currentLng === "kz"}
                                                onClick={() => changeLang("kz")}>KZ</LanguageButton>
                            </LanguageSwitcher>
                        </NavContainer>

                        <BalanceAndProfileContainer>
                            <IconContainer onClick={handlePlusIconClick}>
                                <PlusIcon iconMargin="0 8px 0 0"/>
                                <BalanceText textMargin="0 16px 0 0">
                                    {t("header.balance", {amount: balance.toFixed(0)})}
                                </BalanceText>
                            </IconContainer>

                            <ProfileDropdownContainer ref={dropdownRef}>
                                <ProfileButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                    <InitialsCircle>{getInitials()}</InitialsCircle>
                                    {t("header.cabinet")}
                                </ProfileButton>

                                {isDropdownOpen && (
                                    <DropdownMenu initial={{opacity: 0, y: -20}} animate={{opacity: 1, y: 0}}
                                                  exit={{opacity: 0, y: -20}}>
                                        <DropdownItem href="/PersonalArea">{t("header.menu.profile")}</DropdownItem>
                                        <DropdownItem
                                            href="/RecipientsPrivateCabinet">{t("header.menu.recipients")}</DropdownItem>
                                        <DropdownItem
                                            href="/ChangingContactDetails">{t("header.menu.changeContacts")}</DropdownItem>
                                        <DropdownItem
                                            href="/ChangePassword">{t("header.menu.changePassword")}</DropdownItem>
                                        <DropdownItem onClick={handleLogout}>{t("header.menu.logout")}</DropdownItem>
                                    </DropdownMenu>
                                )}
                            </ProfileDropdownContainer>
                        </BalanceAndProfileContainer>
                    </Header>
                    </div>
                    <div className="block lg:hidden w-full">
                    <MobileHeader>
                        <NavToggler handleLogout={handleLogout}/>
                    </MobileHeader>
                    </div>
                </>
            )}
        </>
    );
};

export default HeaderContainer;
